import { useCallback } from 'react';
import { useNavigation } from '@react-navigation/native';
import Toast from 'react-native-toast-message';
import { useAppDispatch, useAppSelector } from '../../store/store';
import { markRead } from './notificationsSlice';
import {
  selectInboundLifecycleShares,
  selectOutboundLifecycleShares,
  selectShares,
} from '../sharing/sharingSlice';
import type { NotificationItem } from '../../api/types';
import type { RootState } from '../../store/store';

/**
 * Single source of truth for "tap a notification card → navigate".
 *
 * Used by NotificationsScreen, DashboardScreen, and CrewHubScreen so
 * tap behavior is identical across all three surfaces. Replaces the
 * three near-identical inline `handleNotifPress` switch statements
 * that used to drift between screens (the SHARE_RECEIVED bug that
 * shipped in PR #134 only existed because the three copies had
 * subtly different field lookups).
 *
 * Routing strategy:
 *   - **Route by `type` FIRST** for special cases where the same
 *     `target_type` needs different destinations (CREW_INVITE_RECEIVED
 *     has target_type='group' but needs the invite flow, not the
 *     group page — user isn't a member yet).
 *   - Then fall back to `target_type` for the generic mapping.
 *   - For shares, the token is parsed from `action_url` (canonical
 *     per BE-151 spec) with `payload.share_url` and `payload.token`
 *     as defensive fallbacks for forward-compat.
 *
 * Tapping always marks the row read optimistically — the badge clears
 * immediately even before the user picks an action on the destination
 * screen. Server mirrors the read state on the next list fetch.
 */
export function useNotificationDeepLink() {
  const navigation = useNavigation<any>();
  const dispatch = useAppDispatch();
  // PR16 — pulled into the hook so share-related notifications can
  // resolve to an ItemDetail destination when the user already has
  // a matching inventory row on their account (borrower side) OR
  // the outbound share they own carries a known item_id (lender side
  // — via BE#179).
  const inboundLifecycle = useAppSelector(selectInboundLifecycleShares);
  const outboundLifecycle = useAppSelector(selectOutboundLifecycleShares);
  // FE-215 — full share lists (unfiltered by status) so we can detect
  // COMPLETED / CANCELLED / DECLINED shares and toast instead of
  // navigating to a 404 ItemDetail. `selectInbound/OutboundLifecycle`
  // filter to active statuses only; we need the full set here.
  const allShares = useAppSelector(selectShares);
  const inventoryRows = useAppSelector(
    (s: RootState) => s.browse?.inventoryItemsEnhanced ?? [],
  );

  return useCallback(
    (n: NotificationItem) => {
      if (!n.read_at) dispatch(markRead(n.id));

      // 1. Type-first override for GROUP_INVITE_RECEIVED.
      //    BE-151 sets target_type='group' on these, but we need
      //    AcceptInvite, not GroupDetail (the user isn't a member
      //    yet — GroupDetail would render empty/forbidden).
      //    FE-226 — both legacy CREW_INVITE_RECEIVED and the renamed
      //    GROUP_INVITE_RECEIVED route here.
      if (
        n.type === 'CREW_INVITE_RECEIVED' ||
        n.type === 'GROUP_INVITE_RECEIVED'
      ) {
        const token = n.payload?.invite_token as string | undefined;
        if (token) {
          navigation.navigate('AcceptInvite', { token });
        } else {
          navigation.navigate('Notifications');
        }
        return;
      }

      // 2. Default target_type-based routing.
      switch (n.target_type) {
        case 'item':
          if (n.target_id) {
            navigation.navigate('ItemDetail', { itemId: n.target_id });
          }
          return;

        case 'group':
          if (n.target_id) {
            navigation.navigate('GroupDetail', {
              groupId: String(n.target_id),
            });
          }
          return;

        case 'share': {
          // PR16 — Prefer ItemDetail when the share already maps to
          // an inventory row on the viewer's account. Two paths:
          //
          // a) BORROWER side — duplicated row whose
          //    `claimed_from_share_id` matches the share. The user
          //    has already claimed; tap should land on their item.
          // b) LENDER side — outbound share with `item_id` exposed
          //    by BE#179. The lender owns the original; tap should
          //    land on that ItemDetail (e.g. to fire PR12's
          //    Confirm receipt CTA).
          //
          // Falls back to today's ShareClaim(token) flow only when
          // no matching row exists — preserves the pre-claim case.
          const shareId =
            (n.payload?.share_id as number | undefined) ??
            (typeof n.target_id === 'number' ? n.target_id : undefined);

          // FE-215 — friendly toast for terminal-state shares.
          //
          // When a share has been COMPLETED, CANCELLED, or DECLINED,
          // ItemDetail typically 404s because the duplicate row was
          // archived (COMPLETED) or never created (CANCELLED /
          // DECLINED). Pre-check the FE share cache before
          // navigating; if we find a terminal-state match, render a
          // toast and bail. The notification was already marked read
          // above so the badge clears too.
          if (shareId != null) {
            const matchOutbound = (allShares?.shared_by_me ?? []).find(
              (s) => s.share_id === shareId,
            );
            const matchInbound = (allShares?.shared_with_me ?? []).find(
              (s) => s.share_id === shareId,
            );
            const status = (matchOutbound?.status ?? matchInbound?.status) as
              | string
              | undefined;
            if (status === 'COMPLETED') {
              Toast.show({
                type: 'info',
                text1: 'This share has been completed.',
                text2: 'The item was returned to its owner.',
              });
              return;
            }
            if (status === 'CANCELLED') {
              Toast.show({
                type: 'info',
                text1: 'This share was cancelled.',
              });
              return;
            }
            if (status === 'DECLINED') {
              Toast.show({
                type: 'info',
                text1: 'This share was declined.',
              });
              return;
            }
          }

          // (a) borrower-side: scan loaded inventory for the
          // duplicate row carrying claimed_from_share_id.
          if (shareId != null) {
            for (const row of inventoryRows) {
              const fromShare = (row as any).claimed_from_share_id;
              if (fromShare != null && fromShare === shareId) {
                navigation.navigate('ItemDetail', {
                  itemId: (row as any).id,
                });
                return;
              }
            }
            // (b) lender-side: the share is in our outbound list and
            // BE#179 has populated item_id on the row.
            const outbound = outboundLifecycle.find(
              (s) => s.share_id === shareId,
            );
            if (outbound?.item_id != null) {
              navigation.navigate('ItemDetail', {
                itemId: outbound.item_id,
              });
              return;
            }
            // (b-cont) inbound list could also carry item_id post-#179
            // — useful for share-related notifications that arrive
            // before the inventory cache hydrates.
            const inbound = inboundLifecycle.find(
              (s) => s.share_id === shareId,
            );
            if (inbound?.item_id != null) {
              navigation.navigate('ItemDetail', {
                itemId: inbound.item_id,
              });
              return;
            }
          }

          // No matching row → today's claim-screen fallback. BE-151
          // stores the token in `action_url` (canonical) and
          // `payload.share_url` (full URL); older `payload.token` /
          // `payload.share_token` kept as defensive fallbacks.
          const token =
            parseShareToken(n.action_url ?? undefined) ??
            parseShareToken(n.payload?.share_url as string | undefined) ??
            (n.payload?.token as string | undefined) ??
            (n.payload?.share_token as string | undefined);
          if (token) {
            navigation.navigate('ShareClaim', { shareToken: token });
          } else {
            // Token unreachable — surface the row in the inbox so
            // the user at least sees what we couldn't action.
            navigation.navigate('Notifications');
          }
          return;
        }

        case 'borrow_request': {
          // FE-253 — route to the tab that matches the notification's
          // direction. Without this hint BorrowRequestsManager defaults
          // to 'received' and a sender-side notification (APPROVED /
          // DECLINED / FULFILLED / CANCELLED) drops the user on an
          // empty tab because their requests live in 'sent'.
          //
          // BORROW_REQUEST_RECEIVED → user is the OWNER, action needed
          //                            on their items → 'received'
          // BORROW_RETURNED         → item came home to the owner
          //                            → 'received'
          // Everything else under target_type='borrow_request' is
          // sender-side (the requester is being told what happened to
          // their pending request) → 'sent'.
          const senderSideTypes = new Set([
            'BORROW_REQUEST_APPROVED',
            'BORROW_REQUEST_DECLINED',
            'BORROW_REQUEST_FULFILLED',
            'BORROW_REQUEST_CANCELLED',
          ]);
          const initialTab: 'sent' | 'received' = senderSideTypes.has(
            n.type ?? '',
          )
            ? 'sent'
            : 'received';
          navigation.navigate('SharingRequests', { initialTab });
          return;
        }

        case 'pack':
          if (n.target_id) {
            navigation.navigate('PackDetail', { packId: n.target_id });
          }
          return;

        default:
          // Unknown target_type — drop into the inbox so the user
          // can see and action the row in full.
          navigation.navigate('Notifications');
      }
    },
    [
      dispatch,
      navigation,
      inventoryRows,
      inboundLifecycle,
      outboundLifecycle,
      allShares,
    ],
  );
}

/** Pure helper — extract the share token from any `/share/{token}` URL.
 *  Exported for unit tests; the hook is the primary consumer. */
export function parseShareToken(input: string | undefined): string | undefined {
  if (!input) return undefined;
  // Matches both relative `/share/abc` and absolute
  // `https://app.remembr.xyz/share/abc?utm=...` formats.
  const m = input.match(/\/share\/([^/?#]+)/);
  return m ? m[1] : undefined;
}
